import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { AuthContext } from "context/AuthContext";
import { fetchWithParams } from "lib/fetcher";
import { useContext, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import clsx from "clsx";
import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";
import { formatMinute } from "lib/utils";
import { showToast } from "ui/components/common";
import usePlayBackRtcService from "pages/dashboard/playback/service";
import { WsResDataType } from "lib/types";

export default function Timeline({
  disableSwitchArrow,
  cameraId,
  timelineInterval,
  startAtTimeline,
  endAtTimeline,
  setStartAtTimeline,
  setEndAtTimeline,
  playBackStreamId,
  capabilities,
  disableAlertLine,
  onSeekAt,
  progressTime,

  orgId,
}: {
  disableSwitchArrow?: boolean;
  cameraId: number;
  timelineInterval: number;
  startAtTimeline: number;
  endAtTimeline: number;
  setStartAtTimeline: (startAt: number) => void;
  setEndAtTimeline: (endAt: number) => void;
  playBackStreamId: number;
  capabilities: any;
  disableAlertLine?: boolean;
  onSeekAt: (seekPoint: number) => void;
  progressTime: number;
  orgId?: string;
}) {
  const { sendGetPlaybackRecordsRequest } = usePlayBackRtcService();
  const [shouldFetchAlerts, setShouldFetchAlerts] = useState(false);
  const [playbackRecords, setPlaybackRecords] = useState([]);

  const [mousePos, setMousePos] = useState(-1);
  const [timeToolTip, setTimeToolTip] = useState("");

  const { user } = useContext(AuthContext);

  const startAt = useMemo(
    () => new Date(startAtTimeline).setHours(0, 0, 0, 0),
    [startAtTimeline],
  );

  useEffect(() => {
    if (
      !Number.isInteger(capabilities?.streamNum) ||
      playBackStreamId >= capabilities?.streamNum
    ) {
      return;
    }

    const getPlaybackRecords = async () => {
      let _startAt = startAt;
      if (endAtTimeline - _startAt >= 24 * 60 * 60 * 1000) {
        _startAt = endAtTimeline - 24 * 60 * 60 * 1000;
      }
      try {
        const res = await sendGetPlaybackRecordsRequest(
          cameraId,
          playBackStreamId,
          Math.floor(_startAt / 1000),
          _startAt / 1000 + 24 * 60 * 60 - 1,
          orgId || user.orgId,
        );
        const data = (res as WsResDataType)?.playbackGetRecordsReply?.records;
        if (data?.length === 0) {
          showToast(1, "There is no recording in the selected time period.");
        }
        console.log("wsMsgData records ", data);
        setPlaybackRecords(data || []);
      } catch (error) {
        showToast(
          1,
          "There was a problem searching the history records. Please try again. Error: " +
            error?.message,
        );
      }
    };
    getPlaybackRecords();
  }, [startAt, playBackStreamId, capabilities]);

  useEffect(() => {
    if (startAtTimeline && endAtTimeline && cameraId && !disableAlertLine) {
      setShouldFetchAlerts(true);
    }
  }, [startAtTimeline, endAtTimeline, cameraId]);

  const displayRecord = useMemo(() => {
    const res = playbackRecords
      .filter((record) => {
        const outofPeriod =
          startAtTimeline / 1000 >= record.endedAt ||
          endAtTimeline / 1000 <= record.startedAt;
        return !outofPeriod;
      })
      .map((record) => ({
        startedAt: Math.max(record.startedAt, startAtTimeline / 1000),
        endedAt: Math.min(record.endedAt, endAtTimeline / 1000),
      }));

    console.log(
      "displayRecord = ",
      res,
      startAtTimeline,
      endAtTimeline,
      playbackRecords,
    );
    return res;
  }, [playbackRecords, startAtTimeline, endAtTimeline]);

  const { data: dataAlerts } = useSWR(
    shouldFetchAlerts
      ? [
          `/alert/alerts/playback?cameraIds=${cameraId}&startedAt=${startAtTimeline}&endedAt=${endAtTimeline}&paging.limit=1000`,
          user.token,
          user.orgId,
        ]
      : null,
    ([url, token, orgId]) => fetchWithParams(url, token, orgId),
    {
      revalidateOnFocus: false,
    },
  );

  function handleProgressClick(e: any) {
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left; //x position within the element.
    const w = e.target.clientWidth;

    const seekPoint = Math.floor((timelineInterval * 3600 * x) / w);
    console.log("seekPoint = ", seekPoint * 1000 + startAtTimeline);
    //seek absoluteTime
    onSeekAt(seekPoint * 1000 + startAtTimeline);
  }

  function handleProgressMouseMove(e: any) {
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left; //x position within the element.
    const w = e.target.clientWidth;

    const offset = Math.floor((timelineInterval * 3600 * x) / w);
    const mouseMsTimestamp = startAtTimeline + offset * 1000;

    const hasHistory = timeHasHisory(mouseMsTimestamp / 1000);
    if (hasHistory) {
      setTimeToolTip(formatMinute(mouseMsTimestamp));
    } else {
      setTimeToolTip("No history");
    }
    setMousePos(x);
  }

  function handleProgressMouseLeave(e: any) {
    setMousePos(-1);
  }

  function timeHasHisory(sTimestamp: number) {
    return playbackRecords.some(
      (data) => data.startedAt <= sTimestamp && sTimestamp <= data.endedAt,
    );
  }

  function formatHour(msTimestamp: number) {
    const hours = new Date(msTimestamp).getHours();
    return (hours % 12 || 12) + " " + (hours >= 12 ? "PM" : "AM");
  }
  function handleBtnLeftArrow() {
    setEndAtTimeline(startAtTimeline);

    setStartAtTimeline(startAtTimeline - timelineInterval * 3600 * 1000);
  }
  function handleBtnRightArrow() {
    setStartAtTimeline(endAtTimeline);

    setEndAtTimeline(endAtTimeline + timelineInterval * 3600 * 1000);
  }

  const percent =
    ((progressTime - startAtTimeline / 1000) / (timelineInterval * 3600)) * 100;
  return (
    <div className="w-full flex items-center">
      {!disableSwitchArrow && (
        <div className="flex items-center  text-neutral-500 text-sm pr-1 h-[22px] rounded-l-full bg-white">
          <button
            className="rounded-full bg-neutral-200 w-[22px] h-[22px]"
            onClick={handleBtnLeftArrow}
          >
            <ChevronLeftIcon className="p-1" />
          </button>
          <p className="mx-2">{formatHour(startAtTimeline)}</p>
        </div>
      )}
      {disableSwitchArrow && (
        <div className="flex items-center bg-white text-neutral-500 text-sm pl-1 h-[22px] rounded-r-full">
          <p className="mx-2">{formatMinute(startAtTimeline)}</p>
        </div>
      )}
      <div className="grow flex items-center h-[30px] relative ">
        {mousePos >= 0 ? (
          <div
            className={clsx(
              `absolute z-10 w-[80px] h-[25px] bg-slate-800 text-slate-50 text-center rounded-lg text-sm/6`,
            )}
            style={{ left: `${mousePos - 40}px`, top: "-23px" }}
          >
            {timeToolTip}
          </div>
        ) : null}
        {/* valid line */}
        <div className="absolute top-1 left-0 right-0 bottom-0 h-[22px] pointer-events-none overflow-hidden">
          {displayRecord?.map((data: any, index: number) => (
            <div
              key={"record" + index}
              className="absolute bg-neutral-100 h-full"
              style={{
                left: `calc(${(Math.max(data.startedAt - Math.floor(startAtTimeline / 1000), 0) / (timelineInterval * 3600)) * 100}%)`,
                width: `calc(${((data.endedAt - data.startedAt) / (timelineInterval * 3600)) * 100}%)`,
              }}
            ></div>
          ))}
        </div>

        {/* Alert line */}
        <div className="alert-line absolute top-[26px] left-0 right-0 bottom-0 h-[10px] cursor-pointer">
          {dataAlerts &&
            dataAlerts.data &&
            dataAlerts.data.map((data: any, index: number) => {
              const timestamp = +data.startedAt;
              const date = new Date(timestamp);
              const timeString = date.toLocaleTimeString();
              const tooltipHtml = `<div>
              <img src=${data?.snapshot?.url ? data.snapshot.url : ""} width="200" height="113" />
              <p class='text-center'>
                <span>${data.eventTypes.toString()}</span> | 
                <span>${timeString}</span>
              </p>
            </div>`;

              return (
                <div
                  key={"alert" + index}
                  className="absolute bg-indigo-300 h-full rounded-full"
                  style={{
                    left: `calc(${((data.startedAt / 1000 - startAtTimeline / 1000) / (timelineInterval * 3600)) * 100}%)`,
                    width: `calc(${((data.endedAt / 1000 - data.startedAt / 1000 + 1) / (timelineInterval * 3600)) * 100}%)`,
                  }}
                  data-tooltip-id="my-tooltip"
                  data-tooltip-html={tooltipHtml}
                ></div>
              );
            })}
        </div>
        <Tooltip
          id="my-tooltip"
          className="!bg-white !text-black !border-2 !rounded-xl !shadow-xl !p-0 overflow-hidden"
          place="top"
          offset={30}
          noArrow={true}
        />

        {/* Time Interval Line */}
        <div className="absolute top-[10px] w-full ">
          <div
            className="flex justify-between items-center w-full cursor-pointer"
            onMouseDown={handleProgressClick}
            onMouseMove={handleProgressMouseMove}
            onMouseLeave={handleProgressMouseLeave}
          >
            {Array.from(Array(61), (e, i) => {
              if (i === 0 || i === 60) {
                return (
                  <div
                    key={i}
                    className="border-l-2 border-stone-300 h-[12px] pointer-events-none"
                  ></div>
                );
              }
              return i % 5 === 0 ? (
                <div
                  key={i}
                  className="border-l border-stone-300 h-[12px] pointer-events-none"
                ></div>
              ) : (
                <div
                  key={i}
                  className="border-l-2 border-stone-300 h-[6px] pointer-events-none"
                ></div>
              );
            })}
          </div>
        </div>

        {/* Seek Point */}
        <div className="w-full relative  h-full pointer-events-none">
          <div
            className={clsx("absolute", {
              invisible: percent < 0 || percent > 100,
            })}
            style={{
              left: `calc(${percent}% - 5px)`,
            }}
          >
            <div
              className="w-0 h-0 
            border-l-[5px] border-l-transparent
            border-t-[10px] border-t-neutral-500
            border-r-[5px] border-r-transparent"
            ></div>
            <div className="border-l-2 border-neutral-500 h-[30px] w-[2px] ml-[4px] mt-[-2px]"></div>
          </div>
        </div>
      </div>
      {!disableSwitchArrow && (
        <div className="flex items-center bg-white text-neutral-500 text-sm pl-1 h-[22px] rounded-r-full">
          <p className="mx-2">{formatHour(endAtTimeline)}</p>
          <button
            className="rounded-full bg-neutral-200 w-[22px] h-[22px]"
            onClick={handleBtnRightArrow}
          >
            <ChevronRightIcon className="p-1" />
          </button>
        </div>
      )}
      {disableSwitchArrow && (
        <div className="flex items-center bg-white text-neutral-500 text-sm pl-1 h-[22px] rounded-r-full">
          <p className="mx-2">{formatMinute(endAtTimeline)}</p>
        </div>
      )}
    </div>
  );
}
